// Libs
import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
// Utils
import GENERAL from 'utils/constants/general';
import { debounce, formatNumber } from 'utils/libs';
// Components
import {
	Wrapper,
	TableVirtualized,
	Flag,
	Timer,
	TimeRemaining,
	Span,
	Checkbox,
	TextField,
	InputNumber,
	TimePicker,
	DatePicker,
	Dropdown,
	Menu,
	ReportIcon,
	Tooltip,
	Button,
	LogoIcon,
} from 'components';
import {
	OnlyReadField,
	AssignedTechId,
	EventId,
	ZoneId,
	Warranty,
	DocNumber,
	TransactionValue,
	WarehousePackageIdValue,
	StateItemValue,
	DeleteValue,
	ServiceId,
	ContractFrequencyId,
	PaycutName,
	PaymentSummaryId,
	LinesQuantity,
	Applied,
	OrdersTotal,
	OrdersTopay,
	OrdersNotpay,
	OrdersPrevious,
	OrdersPending,
	OrdersPaid,
	Amount,
	Bonus,
	Sync,
} from 'components/DataFieldManager';
import WarehouseSeriesStateValue from '../StateItemValue/WarehouseSeriesStateValue';
import OrderId from '../OrderId/OrderId';
import MergedPDFReport from '../MergedPDFReport/MergedPDFReportContext';
import InventoryTransactionValue from 'core/ElementViewBuilder/views/Inventory/InventoryTransactionValue';
import useOrderApi from 'core/hooks/useCoreApi';
import { reloadDatasource } from 'core/actions';
import { ElementViewBuilderConfig } from 'core/ElementViewBuilder';
import { ElementViewBuilderContext } from 'core/context';
import PaycutSummaryAction from '../PaycutSummaryAction/PaycutSummaryAction';
import PaycutPaymentsAction from '../PaycutPaymentsAction/PaycutPaymentsAction';
import PaymentSummaryAction from '../PaymentSummaryAction/PaymentSummaryAction';
import {
	selectSelectedDataFilterId,
	selectSelectedOrderTargetFilterId,
} from 'core/selectors';
import { selectSelectedSubModule } from 'components/Modules/selectors';
import { useToastMessage } from 'hooks';
import useTheme from 'hooks/useTheme';
import {
	canManageTransactionSignatures,
	canSignTransactionFromFeed,
	getTransactionFlowActions,
	getTransactionSignatureRequestAction,
	resolveTransactionFlowActionsWithCapabilities,
} from 'utils/libs';
// Styles
import styles from './OrderIdCell.module.css';

const { ENV } = GENERAL;

const { onClickRow } = TableVirtualized;
const { Input } = TextField;
const getSelectionItemKey = selected => selected.itemKey || 'id';
const getSelectionItemValue = (selected, rowData) =>
	rowData[getSelectionItemKey(selected)] ??
	rowData?.wtdi_id ??
	rowData?.wtdiId ??
	rowData?.Id;
const getPaymentOverviewName = (field, rowData) =>
	field.nameField
		? rowData[field.nameField]
		: rowData.paid_to_name || rowData.payment_name;
const createOrderMetricCell = OrdersMetric => {
	const OrderMetricCell = ({ field, rowData }) => (
		<OrdersMetric
			field={field}
			elementId={rowData.id}
			name={getPaymentOverviewName(field, rowData)}
			value={rowData[field.id]}
			coin_id={rowData.coin_id}
			paid_to={rowData.paid_to}
			paycut_id={rowData.paycut_id}
			isPaymentSigned={rowData.signed_label === 'Si'}
			isPaymentOpen={rowData.rowDataState === 'open'}
			disableOverview={!!field.disableOverview}
		/>
	);

	return OrderMetricCell;
};

export const DefaultEditableCell = ({
	rowData,
	field,
	fieldActionTypes = {},
}) => {
	const [localValue, setLocalValue] = useState(() => rowData[field.id]);

	const onChange = fieldActionTypes[field.type]?.onChange || function () {};

	const onDebouncedChange = useCallback(
		debounce(value => onChange(value, { rowData, field }), 1000),
		[rowData[field.id]],
	);

	const handleOnChange = value => {
		setLocalValue(value);
		onDebouncedChange(value);
	};

	const getDefaultComponent = {
		string: () => (
			<Input
				value={localValue}
				onChange={e => handleOnChange(e.target.value)}
			/>
		),
		int: () => (
			<InputNumber
				value={localValue}
				onChange={value => handleOnChange(value)}
			/>
		),
		decimal: () => (
			<InputNumber
				value={localValue}
				onChange={value => handleOnChange(value)}
			/>
		),
		date: () => (
			<DatePicker
				allowClear={false}
				value={moment(localValue)}
				onChange={date => handleOnChange(date.utc().format())}
			/>
		),
		datetime: () => (
			<Wrapper padding='0'>
				<DatePicker
					allowClear={false}
					value={moment(localValue)}
					onChange={date => handleOnChange(date.utc().format())}
				/>
				<TimePicker
					allowClear={false}
					value={moment(localValue)}
					onChange={date => handleOnChange(date.utc().format())}
				/>
			</Wrapper>
		),
		time: () => (
			<TimePicker
				allowClear={false}
				value={moment(localValue)}
				onChange={date => {
					setLocalValue(date.format());
					onDebouncedChange(date.utc().format());
				}}
			/>
		),
	}[field.type];

	if (!getDefaultComponent) return null;
	return getDefaultComponent();
};

export const DecimalCell = ({ field, rowData }) => (
	<Span>{formatNumber.new(rowData[field.id], '', 2)}</Span>
);

export const DefaultCell = ({ rowData, field, profile, isCopyValue }) => {
	if (field.formatAsDecimal) {
		return <DecimalCell rowData={rowData} field={field} />;
	}

	return (
		<OnlyReadField
			rowData={rowData}
			field={field}
			profile={profile}
			isCopyValue={isCopyValue}
		/>
	);
};

export const WarehouseNameCell = ({ rowData, field, profile }) => {
	const { setElementView } = React.useContext(ElementViewBuilderContext);
	const selectedDataFilterId = useSelector(selectSelectedDataFilterId);
	const selectedOrderTargetFilterId = useSelector(
		selectSelectedOrderTargetFilterId,
	);
	const selectedSubModule = useSelector(selectSelectedSubModule);
	const elementViewKeys =
		ElementViewBuilderConfig.getAuthorizedElementViewKeys();
	const isIndividualWarehouse =
		selectedSubModule?.key === 'inventory.warehouses.control' &&
		Number(selectedDataFilterId) === 30;

	if (!isIndividualWarehouse) {
		return <DefaultCell rowData={rowData} field={field} profile={profile} />;
	}

	return (
		<Span
			hoverable
			emphasis
			cursor='pointer'
			onClick={event => {
				event.stopPropagation();
				setElementView({
					elementId: rowData.id,
					datasourceAccessId: selectedDataFilterId,
					orderTargetId: selectedOrderTargetFilterId,
					warehouseName: rowData.name,
					elementViewKey: elementViewKeys.WAREHOUSE_STOCK_ELEMENT,
				});
			}}
		>
			{rowData[field.id]}
		</Span>
	);
};

const isClosedTransaction = transaction =>
	Boolean(transaction?.completed ?? transaction?.wtdIsClosed);

export const TransactionSignatureActionCell = ({ rowData, profile }) => {
	const dispatch = useDispatch();
	const sendToast = useToastMessage();
	const { signWtd } = useOrderApi();
	const [isSigning, setIsSigning] = useState(false);
	const localFlowActions = getTransactionFlowActions(rowData, profile);
	const isCapabilityManagedInventory =
		localFlowActions.isNewInventory || localFlowActions.isTransfer;
	const flowActions = resolveTransactionFlowActionsWithCapabilities(
		localFlowActions,
		undefined,
		rowData.id,
	);
	const canShow = isCapabilityManagedInventory
		? flowActions.canSign
		: !isClosedTransaction(rowData) &&
			canManageTransactionSignatures(rowData, profile);
	const canSign = isCapabilityManagedInventory
		? flowActions.canSign
		: canSignTransactionFromFeed(rowData, profile);
	const disabled = !canSign || isSigning;
	const actionLabel = isCapabilityManagedInventory
		? flowActions.signatureLabel
		: 'Firmar';

	if (!canShow) return null;

	const handleSign = event => {
		event.stopPropagation();
		if (disabled) return;

		setIsSigning(true);
		signWtd(rowData.id, getTransactionSignatureRequestAction(flowActions))
			.then(() => {
				sendToast({ message: 'Documento firmado', type: 'success' });
				dispatch(reloadDatasource());
			})
			.catch(error =>
				sendToast({
					message: error.message || 'No fue posible firmar el documento',
					type: 'error',
				}),
			)
			.finally(() => setIsSigning(false));
	};

	if (isSigning) return <LogoIcon spin size='s' />;

	return (
		<Tooltip
			title={
				disabled && !isCapabilityManagedInventory
					? 'Esperando Firma Origen para habilitar la firma destino'
					: undefined
			}
		>
			<span onClick={event => event.stopPropagation()}>
				<Button disabled={disabled} margin='0' onClick={handleSign}>
					{actionLabel}
				</Button>
			</span>
		</Tooltip>
	);
};

export const IndexCell = ({ rowIndex, rowData, field, selected, setState }) => {
	const _onClickRow = filteredItems =>
		setState(prev => ({
			...prev,
			selected: {
				...prev.selected,
				items: filteredItems,
			},
		}));
	const flag = rowData[field.id]?.flag;
	const index = rowIndex + 1;

	return (
		<>
			{flag && <Flag style={flag.style || {}} height='20px' />}
			{field.unselectable ? (
				<Span margin='10px'>{index}</Span>
			) : (
				<Checkbox
					checked={
						selected.items.indexOf(getSelectionItemValue(selected, rowData)) !==
						-1
					}
					onChange={() =>
						onClickRow({
							id: getSelectionItemValue(selected, rowData),
							selected,
							onClickedRow: _onClickRow,
						})
					}
				>
					{index}
				</Checkbox>
			)}
		</>
	);
};

export const PriceCell = ({ rowData }) => {
	const rawPrice = rowData.price;
	if (rawPrice == null) return <Span>-</Span>;
	if (typeof rawPrice === 'string') return <Span>{rawPrice}</Span>;
	const isObject = typeof rawPrice === 'object';
	const priceValue = isObject ? rawPrice.price : rawPrice;
	const symbol = isObject ? rawPrice.symbol : rowData.coinSymbol || '';
	return <Span>{formatNumber.new(priceValue, symbol, 2)}</Span>;
};

export const OrderCountdownCell = ({ rowData }) => {
	const { startAt, timeRemaining, eventProps } = rowData;
	return (
		<Timer
			startAt={startAt}
			timeRemaining={timeRemaining}
			freezeTimer={eventProps?.[ENV.MANAGE_PROPS.FLAGS.FREEZE_TIMER]}
			render={({ timeRemaining } = {}) => (
				<TimeRemaining time={timeRemaining} />
			)}
		/>
	);
};

export const AssignedTechIdCell = ({ field, rowData, selected }) => (
	<AssignedTechId field={field} rowData={rowData} selected={selected} />
);

export const EventIdCell = ({ field, rowData, selected }) => (
	<EventId field={field} rowData={rowData} selected={selected} />
);

export const ZoneIdCell = ({ field, rowData, selected }) =>
	field.canUpdateAddress ? (
		<ZoneId field={field} rowData={rowData} selected={selected} />
	) : (
		<Span>{field.value || rowData[field.name] || rowData[field.id]}</Span>
	);

export const MergedPDFReportCell = ({ rowData }) =>
	!rowData.otds?.length ? null : (
		<Dropdown.Wrapper
			trigger={['click']}
			overlay={
				<Menu.Wrapper>
					{rowData.otds.map(({ id } = {}) => (
						<Menu.Item key={id}>
							<MergedPDFReport resourceId={id} style={'Icon'} />
						</Menu.Item>
					))}
				</Menu.Wrapper>
			}
		>
			<div>
				<ReportIcon selected button />
			</div>
		</Dropdown.Wrapper>
	);

export const WarrantyCell = ({ rowData, field }) => (
	<Warranty rowData={rowData} field={field} />
);
export const DocNumberCell = ({ rowData, field }) => (
	<DocNumber rowData={rowData} field={field} />
);

const OrdersControlOrderIdCell = ({
	rowData,
	field,
	selectionEnabled,
	selectionValue,
	isSelected,
	odtId,
	priority,
	hasTrackingFlag,
	onToggleSelection,
}) => {
	const { theme } = useTheme();
	const className = [
		styles.ordersControlOrderCell,
		selectionEnabled ? styles.selectableOrderCell : null,
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div
			className={className}
			data-orders-control-order-cell=''
			style={{
				'--orders-control-order-hover-color': theme._secondaryColor,
			}}
		>
			<div
				className={styles.trackingMarkerSlot}
				data-order-tracking-slot=''
				aria-hidden={hasTrackingFlag ? undefined : true}
			>
				{hasTrackingFlag && (
					<Flag
						style={priority.style}
						height='20px'
						title={priority.reason}
						role={priority.reason ? 'img' : undefined}
						aria-label={priority.reason}
						aria-hidden={priority.reason ? undefined : true}
					/>
				)}
			</div>
			{selectionEnabled && (
				<span onClick={event => event.stopPropagation()}>
					<Checkbox
						checked={isSelected}
						aria-label={`${
							isSelected ? 'Deseleccionar' : 'Seleccionar'
						} orden ${odtId || selectionValue}`}
						tabIndex={0}
						margin='0 8px 0 0'
						onChange={onToggleSelection}
					/>
				</span>
			)}
			{rowData.orderFlag && (
				<Flag style={rowData.orderFlag.style || {}} height='20px' />
			)}
			<div
				className={styles.orderIdentifier}
				data-order-identifier=''
				title={odtId == null ? undefined : String(odtId)}
			>
				<OrderId
					elementId={rowData.order_id || rowData.id}
					odtId={odtId}
					disableOverview={field.disableOverview}
					orderOverviewMode={field.orderOverviewMode}
				/>
			</div>
		</div>
	);
};

export const OrderIdCell = ({ rowData, field, selected, setState }) => {
	const isOrdersControlPrimaryField = field.ordersControlPrimaryField === true;
	const selectionEnabled =
		isOrdersControlPrimaryField &&
		field.ordersControlSelectionEnabled !== false;
	const selectionValue = selectionEnabled
		? getSelectionItemValue(selected, rowData)
		: undefined;
	const isSelected =
		selectionEnabled && selected.items.includes(selectionValue);
	const odtId = rowData.odtId || rowData.odt_id;
	const priority = rowData.priority;
	const hasTrackingFlag =
		isOrdersControlPrimaryField && priority?.style != null;

	const onToggleSelection = () =>
		onClickRow({
			id: selectionValue,
			selected,
			onClickedRow: filteredItems =>
				setState(prev => ({
					...prev,
					selected: { ...prev.selected, items: filteredItems },
				})),
		});

	if (isOrdersControlPrimaryField) {
		return (
			<OrdersControlOrderIdCell
				rowData={rowData}
				field={field}
				selectionEnabled={selectionEnabled}
				selectionValue={selectionValue}
				isSelected={isSelected}
				odtId={odtId}
				priority={priority}
				hasTrackingFlag={hasTrackingFlag}
				onToggleSelection={onToggleSelection}
			/>
		);
	}

	return (
		<>
			{rowData.orderFlag && (
				<Flag style={rowData.orderFlag.style || {}} height='20px' />
			)}
			<OrderId
				elementId={rowData.order_id || rowData.id}
				odtId={odtId}
				disableOverview={field.disableOverview}
				orderOverviewMode={field.orderOverviewMode}
			/>
		</>
	);
};

export const DeleteCell = ({ rowData, field, selected }) => (
	<DeleteValue rowData={rowData} field={field} selected={selected} />
);

export const InventoryTransactionValueCell = ({ rowData, field }) => (
	<InventoryTransactionValue
		rowData={rowData}
		setState={field.setState}
		cart={field.cart}
	/>
);
export const TransactionValueCell = ({ rowData, stateTable, field }) => (
	<TransactionValue rowData={rowData} stateTable={stateTable} field={field} />
);
export const WarehousePackageCell = ({ field, rowData, stateTable }) => {
	return (
		<WarehousePackageIdValue
			field={field}
			rowData={rowData}
			stateTable={stateTable}
		/>
	);
};
export const StateItemCell = ({ rowData, stateTable, field }) => {
	return field.seriesStateEditor ? (
		<WarehouseSeriesStateValue rowData={rowData} field={field} />
	) : (
		<StateItemValue rowData={rowData} stateTable={stateTable} />
	);
};

export const ServiceIdCell = ({ field, rowData, selected }) => (
	<ServiceId field={field} rowData={rowData} selected={selected} />
);

export const ContractFrequencyIdCell = ({ field, rowData }) => (
	<ContractFrequencyId field={field} rowData={rowData} />
);

export const PaycutNameCell = ({ field, rowData }) => (
	<PaycutName
		elementId={rowData.id}
		paycutName={rowData[field.id]}
		disableOverview={field.disableOverview}
	/>
);
export const PaycutPaymentsActionCell = ({ rowData }) => (
	<PaycutPaymentsAction paycutId={rowData.id} />
);
export const IdCell = ({ field, rowData, profile }) => {
	const getColorState = state =>
		({
			open: { title: 'Abierto', style: { backgroundColor: 'white' } },
			ready: { title: 'Listo', style: { backgroundColor: 'darkcyan' } },
			agreed: { title: 'Aprobado', style: { backgroundColor: 'deepskyblue' } },
			released: { title: 'Realizado', style: { backgroundColor: 'yellow' } },
			paid: { title: 'Pagado', style: { backgroundColor: 'green' } },
		})[state];

	const stateKey = rowData.rowDataState || rowData.state;

	return (
		<>
			{stateKey && (
				<Tooltip title={getColorState(stateKey).title}>
					<Flag
						style={getColorState(stateKey).style}
						height='20px'
						marginRight='5px'
					/>
				</Tooltip>
			)}
			{field.opensPaycutSummary ? (
				<PaycutSummaryAction paycutId={rowData.id}>
					{rowData[field.id]}
				</PaycutSummaryAction>
			) : field.opensPaymentSummary ? (
				<PaymentSummaryAction payment={rowData}>
					{rowData[field.id]}
				</PaymentSummaryAction>
			) : (
				<PaymentSummaryId rowData={rowData} field={field} profile={profile} />
			)}
		</>
	);
};

export const LinesQuantityCell = ({ field, rowData }) => (
	<LinesQuantity
		field={field}
		paid_to={rowData.paid_to}
		elementId={rowData.id}
		name={getPaymentOverviewName(field, rowData)}
		paycut_id={rowData.paycut_id}
		isPaymentSigned={rowData.signed_label === 'Si'}
		isPaymentOpen={rowData.rowDataState === 'open'}
		linesQuantityValue={rowData.lines_quantity}
		disableOverview={field.disableOverview}
	/>
);

export const AppliedCell = ({ field, rowData }) => (
	<Applied field={field} rowData={rowData} />
);

export const OrdersTotalCell = createOrderMetricCell(OrdersTotal);
export const OrdersTopayCell = createOrderMetricCell(OrdersTopay);
export const OrdersNotpayCell = createOrderMetricCell(OrdersNotpay);
export const OrdersPreviousCell = createOrderMetricCell(OrdersPrevious);
export const OrdersPendingCell = createOrderMetricCell(OrdersPending);
export const OrdersPaidCell = createOrderMetricCell(OrdersPaid);
export const AmountCell = ({ field, rowData, stateTable }) => (
	<Amount
		field={field}
		rowData={rowData}
		stateTable={stateTable}
		value={rowData[field.id]}
	/>
);

export const StateCell = ({ rowData }) => {
	const stateLabels = {
		open: 'Abierto',
		ready: 'Listo',
		agreed: 'Aprobado',
		released: 'Realizado',
		paid: 'Pagado',
	};
	return <Span>{stateLabels[rowData.state] || rowData.state}</Span>;
};
export const BonusCell = ({ field, rowData }) => (
	<Bonus field={field} rowData={rowData} value={rowData[field.id]} />
);
export const SyncCell = ({ field, rowData }) => (
	<Sync field={field} rowData={rowData} value={rowData[field.id]} />
);
